import { Flexbox, Heading, Loader, Search, Button, Icons, Dialog, Spacer, Spacings, useToast } from '@skf-internal/ui-components-react-legacy';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { useAppSelector } from 'store';
import ManufacturerTable from './ManufacturerTable';
import { useEffect, useState } from 'react';
import { Manufacturer } from 'domain/shared/Manufacturer';
import { getManufacturers, deleteManufacturer } from 'services/api/master-data.service';
import OffsetLimitPagination from 'components/pagination/OffsetLimitPagination';
import { useDebounce } from 'utils/hooks/useDebounce';

const ManufacturerResult = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const loading = useAppSelector((state) => state.searchData.loading);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { addToast } = useToast();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [manufacturerToDelete, setManufacturerToDelete] = useState<number | null>(null);

  const backendSortMap: { [key: string]: string } = {
    manufacturerName: 'Manufacturer Name',
    sWtg: 'sWTG',
    wtg: 'WTG',
    gearbox: 'Gearbox',
    generator: 'Generator',
    country: 'Country'
  };

  const fetchManufacturers = () => {
    const sortParam = sortField && sortOrder ? `${backendSortMap[sortField]}:${sortOrder}` : undefined;
    getManufacturers(offset, limit, debouncedSearchTerm, sortParam).then((res) => {
      setManufacturers(res.data.rows);
      setTotal(res.data.count);
    });
  };

  useEffect(() => {
    fetchManufacturers();
  }, [offset, limit, debouncedSearchTerm, sortField, sortOrder]);

  const onPageChange = (newOffset: number, newLimit: number) => {
    setOffset(newOffset);
    setLimit(newLimit);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setOffset(0); // Reset offset when search term changes
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
    setOffset(0); // Reset offset when sort changes
  };

  const openDeleteDialog = (id: number) => {
    setManufacturerToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setManufacturerToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  const confirmDelete = async () => {
    if (manufacturerToDelete !== null) {
      try {
        await deleteManufacturer(manufacturerToDelete);
        addToast({ children: intl.formatMessage({ id: 'masterdata.manufacturer.delete.success' }), feSeverity: 'success' });
        fetchManufacturers();
      } catch (error: any) {
        console.error('Failed to delete manufacturer', error);
        addToast({ children: intl.formatMessage({ id: 'masterdata.manufacturer.delete.failure' }, { message: error.message }), feSeverity: 'error' });
      } finally {
        closeDeleteDialog();
      }
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <Flexbox feJustifyContent="center" className="mb-3">
        <Search className="w-full" feLabel={'Search'} feHideLabel placeholder={intl.formatMessage({ id: 'masterdata.manufacturer.searchPlaceholder' })} value={searchTerm} onChange={handleSearchChange} />
      </Flexbox>
      <Flexbox feJustifyContent="space-between" className="mb-3">
        <Heading as={'h1'}>{intl.formatMessage({ id: 'masterdata.manufacturer.heading' })}</Heading>
        <Button
          feIcon={{
            feIcon: Icons.Plus,
            position: 'left'
          }}
          feSize="sm"
          onClick={() => {
            console.log('Navigating to add manufacturer page');
            navigate('/master-data/manufacturers/add');
          }}
        >
          {intl.formatMessage({ id: 'button.addNew' })}
        </Button>
      </Flexbox>
      <Flexbox feJustifyContent="right" className="mb-3">
        <></>
        <OffsetLimitPagination totalItems={total} limit={limit} offset={offset} onPageChange={onPageChange} />
      </Flexbox>
      <ManufacturerTable manufacturers={manufacturers} onSortChange={handleSortChange} sortField={sortField} sortOrder={sortOrder} onDelete={openDeleteDialog} />
      <Flexbox feJustifyContent="flex-end" className="mt-3">
        <OffsetLimitPagination totalItems={total} limit={limit} offset={offset} onPageChange={onPageChange} />
      </Flexbox>
      <Dialog feInterruptive open={isDeleteDialogOpen} feTitle={intl.formatMessage({ id: 'masterdata.manufacturer.delete.confirm.heading' })} feOnClose={closeDeleteDialog}>
        {intl.formatMessage({ id: 'masterdata.manufacturer.delete.confirm.message' })}
        <Spacer />
        <Flexbox feGap={Spacings.Md} feJustifyContent="flex-end">
          <Button feType="secondary" onClick={closeDeleteDialog}>
            {intl.formatMessage({ id: 'button.cancel' })}
          </Button>
          <Button onClick={confirmDelete}>{intl.formatMessage({ id: 'button.confirm' })}</Button>
        </Flexbox>
      </Dialog>
    </>
  );
};

export default ManufacturerResult;
